import {
  type APIMessage,
  can,
  ChannelType,
  PermissionFlags,
  THREAD_CHANNEL_TYPES,
} from '@alternativamais/chat-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { gravarPreferencia, lerPreferencia } from '../../armazenamento/local';
import { Icone } from '../../componentes/Icone';
import { useEstado } from '../../chat/contexto';
import { type EstadoBusca, ResultadosBusca } from './busca/ResultadosBusca';
import { Cabecalho } from './cabecalho/Cabecalho';
import { Conversa } from './Conversa';
import { MembrosGrupo } from './dms/MembrosGrupo';
import { Forum } from './forum/Forum';
import { Membros } from './Membros';
import { PainelPerfil } from './perfil/PainelPerfil';
import { FaixaArquivada } from './threads/FaixaArquivada';
import { NovaThread } from './threads/NovaThread';
import { PainelThread } from './threads/PainelThread';
import { BotaoGaveta, useJanelaEstreita } from '../../chat/gaveta';

/** Endereço do canal (ou DM) no app. */
export function rotaDoCanal(canal: { id: string; guild_id?: string | null }) {
  return `/canais/${canal.guild_id ?? '@me'}/${canal.id}`;
}

/**
 * Um canal, thread em tela cheia, DM ou grupo: cabeçalho, a conversa e o
 * painel da direita (membros, thread aberta ao lado, nova thread, busca ou
 * o perfil da pessoa na DM). Fórum tem a sua própria tela.
 */
export function Canal({
  canalId,
  mensagemInicial,
  threadId,
}: {
  canalId: string;
  mensagemInicial?: string;
  /** Thread aberta ao lado (`/canais/:servidor/:canal/threads/:thread`). */
  threadId?: string;
}) {
  const navegar = useNavigate();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const podeVer = useEstado((estado) =>
    estado.channels[canalId]?.guild_id
      ? can(estado, canalId, PermissionFlags.VIEW_CHANNEL)
      : true,
  );
  const podeCriarThread = useEstado(
    (estado) =>
      !!estado.channels[canalId]?.guild_id &&
      !THREAD_CHANNEL_TYPES.has(estado.channels[canalId]?.type ?? 0) &&
      can(estado, canalId, PermissionFlags.CREATE_PUBLIC_THREADS),
  );
  const [lateralAberta, setLateralAberta] = useState(() =>
    lerPreferencia('membros-abertos', true),
  );
  const estreita = useJanelaEstreita();
  const [busca, setBusca] = useState<EstadoBusca | null>(null);
  const [novaThread, setNovaThread] = useState<{
    mensagem: APIMessage | null;
  } | null>(null);

  if (!canal) {
    return (
      <Aviso
        titulo="Canal não encontrado"
        texto="Ele pode ter sido apagado ou você não tem acesso."
      />
    );
  }
  if (!podeVer) {
    return (
      <Aviso
        titulo="Sem acesso"
        texto="Você não tem permissão para ver este canal."
      />
    );
  }
  if (
    canal.type === ChannelType.GUILD_FORUM ||
    canal.type === ChannelType.GUILD_MEDIA
  ) {
    return <Forum forumId={canalId} postId={threadId} />;
  }

  const thread = THREAD_CHANNEL_TYPES.has(canal.type);
  const grupo = canal.type === ChannelType.GROUP_DM;
  const dm = canal.type === ChannelType.DM;
  const alternarLateral = () => {
    setLateralAberta(!lateralAberta);
    gravarPreferencia('membros-abertos', !lateralAberta);
  };
  const fecharThread = () => navegar(rotaDoCanal(canal));

  let painel = null;
  if (threadId) {
    painel = (
      <PainelThread
        key={threadId}
        threadId={threadId}
        aoFechar={fecharThread}
      />
    );
  } else if (novaThread) {
    painel = (
      <NovaThread
        canalId={canalId}
        mensagem={novaThread.mensagem}
        aoFechar={() => setNovaThread(null)}
        aoCriar={(criada) => {
          setNovaThread(null);
          navegar(`${rotaDoCanal(canal)}/threads/${criada}`);
        }}
      />
    );
  } else if (busca) {
    painel = (
      <ResultadosBusca
        canalId={canalId}
        busca={busca}
        aoMudar={setBusca}
        aoFechar={() => setBusca(null)}
      />
    );
  } else if (lateralAberta && !estreita) {
    if (dm && canal.recipients?.[0]) {
      painel = <PainelPerfil userId={canal.recipients[0].id} />;
    } else if (grupo) {
      painel = <MembrosGrupo canalId={canalId} />;
    } else if (canal.guild_id) {
      painel = <Membros guildId={canal.guild_id} canalId={canalId} />;
    }
  }

  return (
    <div className="flex min-w-0 grow flex-col bg-chat">
      <Cabecalho
        canalId={canalId}
        lateralAberta={lateralAberta}
        aoAlternarLateral={alternarLateral}
        textoDaBusca={busca?.texto ?? ''}
        aoBuscar={(texto) =>
          setBusca(
            texto.trim()
              ? { texto, pagina: 1, ordem: 'recentes', tipo: 'mensagens' }
              : null,
          )
        }
        aoCriarThread={
          podeCriarThread ? () => setNovaThread({ mensagem: null }) : undefined
        }
      />
      <div className="relative flex min-h-0 grow">
        <Conversa
          key={canalId}
          canalId={canalId}
          mensagemInicial={mensagemInicial}
          criarThread={
            podeCriarThread
              ? (mensagem) => setNovaThread({ mensagem })
              : undefined
          }
          antesDoCompositor={
            thread ? <FaixaArquivada threadId={canalId} /> : undefined
          }
        />
        {painel && estreita ? (
          // Janela estreita: thread, busca e nova thread cobrem a conversa.
          <div className="absolute inset-0 z-10 flex *:w-full! *:max-w-none!">
            {painel}
          </div>
        ) : (
          painel
        )}
      </div>
    </div>
  );
}

export function Aviso({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="relative flex grow flex-col items-center justify-center gap-2 bg-chat p-8 text-center">
      <span className="absolute top-2 left-4">
        <BotaoGaveta />
      </span>
      <Icone nome="cadeado" tamanho={40} className="text-texto-3" />
      <h2 className="m-0 font-titulo text-2xl font-bold">{titulo}</h2>
      <p className="m-0 text-[15px] text-texto-2">{texto}</p>
    </div>
  );
}
